<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import api from "../../api/axios";
import DataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";

import ArgonButton from "@/components/ArgonButton.vue";

const router = useRouter();

const tasks = ref([]);
const cargando = ref(true);
const soloPendientes = ref(true);

const headers = [
  { text: "Tarea", value: "title" },
  { text: "Contacto", value: "contact_name" },
  { text: "Vence", value: "due_at" },
  { text: "Estado", value: "estado", sortable: false },
  { text: "Acciones", value: "actions", sortable: false }
];

const vencida = (task) => {
  return !task.completed_at && task.due_at && new Date(task.due_at) < new Date();
};

const filteredTasks = computed(() => {
  const lista = soloPendientes.value ? tasks.value.filter(t => !t.completed_at) : tasks.value;

  return [...lista].sort((a, b) => {
    if (!!a.completed_at !== !!b.completed_at) return a.completed_at ? 1 : -1;
    if (!a.due_at) return 1;
    if (!b.due_at) return -1;
    return new Date(a.due_at) - new Date(b.due_at);
  });
});

const getTasks = async () => {
  const res = await api.get("/crm/tasks");
  tasks.value = res.data.tasks;
};

const completarTarea = async (task) => {
  try {
    await api.patch(`/crm/tasks/${task.id}/complete`);
    await getTasks();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo completar la tarea",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
};

const verContacto = (task) => {
  if (task.contact_id) router.push(`/crm/contacts/${task.contact_id}`);
};

const formatFecha = (fecha) => {
  if (!fecha) return "Sin fecha";
  return new Date(fecha).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
};

onMounted(async () => {
  cargando.value = true;
  try {
    await getTasks();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron cargar las tareas",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    cargando.value = false;
  }
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0 tasks-header-title">
        <i class="bi bi-check2-square me-2"></i>
        Tareas
      </h4>

      <div class="form-check form-switch">
        <input id="soloPendientes" v-model="soloPendientes" class="form-check-input" type="checkbox" role="switch">
        <label class="form-check-label tasks-solo-pendientes-label" for="soloPendientes">Solo pendientes</label>
      </div>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <DataTable :headers="headers" :items="filteredTasks" :rows-per-page="20">
          <template #item-title="{ title }">
            <span>{{ title }}</span>
          </template>

          <template #item-contact_name="item">
            <span
              v-if="item.contact_id"
              class="text-primary"
              style="cursor: pointer"
              @click="verContacto(item)"
            >
              {{ item.contact_name || "—" }}
            </span>
            <span v-else class="text-muted">—</span>
          </template>

          <template #item-due_at="{ due_at }">
            {{ formatFecha(due_at) }}
          </template>

          <template #item-estado="item">
            <span v-if="item.completed_at" class="badge bg-success">Completada</span>
            <span v-else-if="vencida(item)" class="badge bg-danger">Vencida</span>
            <span v-else class="badge bg-gradient-secondary">Pendiente</span>
          </template>

          <template #item-actions="item">
            <argon-button
              v-if="!item.completed_at"
              size="sm"
              color="success"
              @click="completarTarea(item)"
            >
              <i class="bi bi-check-lg me-1"></i>Completar
            </argon-button>
          </template>
        </DataTable>
      </div>
    </div>

  </div>
</template>

<style scoped>
.tasks-header-title,
.tasks-solo-pendientes-label {
  color: #fff !important;
}
</style>
